/* eslint-disable @typescript-eslint/no-empty-function */
/* eslint-disable @typescript-eslint/no-explicit-any */

import {
  LoggerApiHandler,
  LoggerInternalApi,
  LoggerInternalApiHandler,
  LoggerLevel,
  LoggerMetadata,
  LoggerPrinter,
  LoggerSeverityLevel,
} from './logger.types';

export function getConsolePrinters(consoleApi: Console, binding?: boolean): Record<LoggerLevel, LoggerPrinter> {
  return {
    FATAL: getConsolePrinter(consoleApi, 'FATAL', binding),
    ERROR: getConsolePrinter(consoleApi, 'ERROR', binding),
    WARN: getConsolePrinter(consoleApi, 'WARN', binding),
    INFO: getConsolePrinter(consoleApi, 'INFO', binding),
    DEBUG: getConsolePrinter(consoleApi, 'DEBUG', binding),
    SILENT: getConsolePrinter(consoleApi, 'SILENT', binding),
  };
}

/**
 * Returns console printer for given level.
 *
 * In some browsers like Internet Explorer, the `console` object is only exposed
 * when the developer tools are opened (source: https://stackoverflow.com/a/5473193)).
 * That behavior can create weird issues because we open the developer tools to investigate
 * but everythings will works well (until the developer tools will be closed and page refreshed).
 * The purpose of this helper is to avoid that behavior and weird issues.
 */
export function getConsolePrinter(consoleApi: Console, level: LoggerLevel, binding?: boolean): LoggerPrinter {
  const noopWriter: LoggerPrinter = () => {};

  if (!consoleApi) {
    return noopWriter;
  }

  let printer: LoggerPrinter;

  if (level === 'FATAL' || level === 'ERROR') {
    printer = consoleApi.error;
  } else if (level === 'WARN') {
    printer = consoleApi.warn || consoleApi.error;
  } else if (level === 'INFO') {
    printer = consoleApi.info || consoleApi.log;
  } else if (level === 'DEBUG') {
    printer = consoleApi.debug || consoleApi.log;
  } else {
    printer = consoleApi.log;
  }

  if (!printer) {
    printer = noopWriter;
  } else if (binding) {
    printer = printer.bind(consoleApi);
  }

  return printer;
}

export function makeLoggerInternalApi(
  level: LoggerLevel,
  handler: LoggerApiHandler,
  printers: Record<LoggerLevel, LoggerPrinter>,
): LoggerInternalApi {
  const noopHandler: LoggerInternalApiHandler = () => {};

  if (level === 'SILENT') {
    return {
      fatal: noopHandler,
      error: noopHandler,
      warn: noopHandler,
      info: noopHandler,
      debug: noopHandler,
    };
  }

  const severityLevel = LoggerSeverityLevel[level];
  const makeLevelHandler =
    (loggerLevel: LoggerLevel): LoggerInternalApiHandler =>
    (metadata, message, optionalParams) =>
      LoggerSeverityLevel[loggerLevel] > severityLevel
        ? noopHandler
        : handler(printers[loggerLevel], loggerLevel, metadata, message, optionalParams);

  return {
    fatal: makeLevelHandler('FATAL'),
    error: makeLevelHandler('ERROR'),
    warn: makeLevelHandler('WARN'),
    info: makeLevelHandler('INFO'),
    debug: makeLevelHandler('DEBUG'),
  };
}

export function formatLoggerMessage(message: any): string {
  if (message instanceof Error && message.stack) {
    message = message.stack;
  }

  if (typeof message !== 'string') {
    try {
      message = JSON.stringify(message);
      // eslint-disable-next-line no-empty
    } catch (_errIgnored) {}
  }

  return `${message}`;
}

export function logEntryDefault(
  printer: LoggerPrinter,
  level: LoggerLevel,
  metadata: LoggerMetadata,
  message: any,
  optionalParams: any[],
): void {
  let output = `${level}|`;

  if (metadata.context) {
    output += `[${metadata.context}]`;
  }

  output += formatLoggerMessage(message);

  printer(output, ...(optionalParams || []));
}

export function logEntryForProduction(
  app: string,
  service: string,
  customMetadata: () => LoggerMetadata = () => ({}),
): LoggerApiHandler {
  return (printer, level, metadata, message, optionalParams) => {
    const output = [`time=${new Date().toISOString()}`, `level=${level}`, `app_id=${app}`, `component_id=${service}`];

    metadata = { ...customMetadata(), ...metadata };

    if (metadata?.ip) {
      output.push(`ip=${metadata.ip}`);
    }

    if (metadata?.correlationId) {
      output.push(`corr_id=${metadata.correlationId}`);
    }

    if (metadata?.context) {
      output.push(`controller_id=${metadata.context}`);
    }

    if (metadata?.route) {
      output.push(`controller_route=${metadata.route}`);
    }

    output.push(`msg=${formatLoggerMessage(message)}`);

    printer(output.join('|'), ...(optionalParams || []));
  };
}
